// ============================================================
// THE VOX (v8.363.1) - what a line at the table IS.
//
// A message is from a PERSON (uid; the seat and the name are snapshots
// for the log), optionally spoken AS their acting combatant, to an
// AUDIENCE the rules enforce: '*' everyone, '@players' every member who
// is not the GM, or uids. The conversation key says where the sender
// aimed it; the audience is what the server reads. Pure types and the
// wire shape - no React, no backend.
// ============================================================

/** Everyone at the table. */
export const TO_ALL = '*';
/** Every member who is not the GM - the default private "Players" channel. */
export const TO_PLAYERS = '@players';

export type ConversationKey = 'table' | 'players' | `channel:${string}` | `dm:${string}`;

type ChatKind = 'say' | 'emote' | 'roll' | 'handout' | 'system' | 'removed';

/** The acting combatant a line is spoken as - name and livery colour (a name or a hex). */
export interface SpeakAs {
  name: string;
  livery: string | null;
}

export interface RollPayload {
  expr: string;
  total: number;
  results: number[];
}

export interface ChatMessage {
  id: string;
  ts: number;
  fromUid: string;
  fromSeat: string;
  fromName: string;
  speakAs: SpeakAs | null;
  kind: ChatKind;
  text: string;
  conv: ConversationKey;
  audience: string[];
  mentions: string[];
  roll: RollPayload | null;
  handoutId: string;
}

/** The document as it travels: `speakAs` and `roll` are stringified - text, never json. */
export interface ChatMessageDoc {
  ts: number;
  fromUid: string;
  fromSeat: string;
  fromName: string;
  speakAs: string;
  kind: string;
  text: string;
  conv: string;
  audience: string[];
  mentions: string[];
  roll: string;
  handoutId: string;
}

export interface ChatChannel {
  id: string;
  name: string;
  members: string[];
  gmListens: boolean;
  createdBy: string;
  createdAt: number;
  /** The sheet team it was seeded from, or ''. */
  fromTeam: string;
}

/** Somebody at the table, as the composer and the chips know them. */
export interface Person {
  uid: string;
  name: string;
  isGm: boolean;
  /** The combatant they are acting as, if published. */
  actingAsName?: string | null;
}

export const MAX_TEXT = 4000;

export function toDoc(m: ChatMessage): ChatMessageDoc {
  return {
    ts: m.ts,
    fromUid: m.fromUid,
    fromSeat: m.fromSeat,
    fromName: m.fromName,
    speakAs: m.speakAs ? JSON.stringify(m.speakAs) : '',
    kind: m.kind,
    text: m.text,
    conv: m.conv,
    audience: m.audience,
    mentions: m.mentions,
    roll: m.roll ? JSON.stringify(m.roll) : '',
    handoutId: m.handoutId,
  };
}

/** Tolerant of a partial or hand-edited document: every field defaulted. */
export function fromDoc(id: string, d: Partial<ChatMessageDoc> | null | undefined): ChatMessage {
  return {
    id,
    ts: typeof d?.ts === 'number' ? d.ts : 0,
    fromUid: d?.fromUid ?? '',
    fromSeat: d?.fromSeat ?? '',
    fromName: d?.fromName ?? '',
    speakAs: parseJson<SpeakAs>(d?.speakAs),
    kind: KINDS.includes(d?.kind as ChatKind) ? (d!.kind as ChatKind) : 'say',
    text: d?.text ?? '',
    conv: (d?.conv as ConversationKey) || 'table',
    audience: stringsIn(d?.audience),
    mentions: stringsIn(d?.mentions),
    roll: parseJson<RollPayload>(d?.roll),
    handoutId: d?.handoutId ?? '',
  };
}

const KINDS: ChatKind[] = ['say', 'emote', 'roll', 'handout', 'system', 'removed'];

/** A stringified field read back, or null when it is absent or not JSON. */
function parseJson<T>(s: unknown): T | null {
  if (typeof s !== 'string' || !s) return null;
  try {
    return JSON.parse(s) as T;
  } catch {
    return null;
  }
}

/** The strings in a stored array; anything else there is dropped. */
function stringsIn(v: unknown): string[] {
  return Array.isArray(v) ? v.filter((a): a is string => typeof a === 'string') : [];
}
